/** UI state shape for the customer's current admission row (Create/Update customer forms). */

export const emptyAdmissionRecordRow = () => ({
  admissionDate: null,
  admissionNumber: '',
  admittedFrom: '',
  admittedFromOther: '',
  enrolledDate: null,
  referralSource: '',
  referralSourceOther: '',
});

/** Prior admission cycles (max 4 additional rows with current = 5 total). */
export const emptyPriorAdmissionRecordRow = () => ({
  admissionDate: null,
  admittedFrom: '',
  admittedFromOther: '',
  dischargeDate: null,
  dischargeReason: '',
  dischargeReasonOther: '',
});

/**
 * @param {object} customer API customer document
 * @param {(s: string) => Date|null} parseDateFn e.g. parseDateFromBackend
 */
export const admissionRecordsStateFromCustomer = (customer, parseDateFn) => {
  const parse = parseDateFn;
  if (Array.isArray(customer?.admission_records) && customer.admission_records.length > 0) {
    return customer.admission_records.map((r, idx) => {
      if (idx === 0) {
        return {
          admissionDate: parse(r.admission_date),
          admissionNumber: r.admission_number || '',
          admittedFrom: r.admitted_from || '',
          admittedFromOther: r.admitted_from_other || '',
          enrolledDate: parse(r.enrolled_date),
          referralSource: r.referral_source || '',
          referralSourceOther: r.referral_source_other || '',
        };
      }
      return {
        admissionDate: parse(r.admission_date),
        admittedFrom: r.admitted_from || '',
        admittedFromOther: r.admitted_from_other || '',
        dischargeDate: parse(r.discharge_date),
        dischargeReason: r.discharge_reason || '',
        dischargeReasonOther: r.discharge_reason_other || '',
      };
    });
  }
  const hasLegacy =
    customer?.admission_date ||
    customer?.admission_number ||
    customer?.admitted_from ||
    customer?.enrolled_date ||
    customer?.referral_source;
  if (hasLegacy) {
    return [
      {
        admissionDate: parse(customer.admission_date),
        admissionNumber: customer.admission_number || '',
        admittedFrom: customer.admitted_from || '',
        admittedFromOther: customer.admitted_from_other || '',
        enrolledDate: parse(customer.enrolled_date),
        referralSource: customer.referral_source || '',
        referralSourceOther: customer.referral_source_other || '',
      },
    ];
  }
  return [emptyAdmissionRecordRow()];
};

export const sanitizeAdmissionRecordsForApi = (records, formatDateForBackend) =>
  (records || []).map((r, idx) => {
    if (idx === 0) {
      return {
        admission_date: formatDateForBackend(r.admissionDate) || '',
        admission_number: r.admissionNumber || '',
        admitted_from: r.admittedFrom || '',
        admitted_from_other: r.admittedFrom === 'other' ? r.admittedFromOther || '' : '',
        enrolled_date: formatDateForBackend(r.enrolledDate) || '',
        referral_source: r.referralSource || '',
        referral_source_other: r.referralSource === 'other' ? r.referralSourceOther || '' : '',
        discharge_date: '',
        discharge_reason: '',
        discharge_reason_other: '',
      };
    }
    return {
      admission_date: formatDateForBackend(r.admissionDate) || '',
      admission_number: '',
      admitted_from: r.admittedFrom || '',
      admitted_from_other: r.admittedFrom === 'other' ? r.admittedFromOther || '' : '',
      enrolled_date: '',
      referral_source: '',
      referral_source_other: '',
      discharge_date: formatDateForBackend(r.dischargeDate) || '',
      discharge_reason: r.dischargeReason || '',
      discharge_reason_other: r.dischargeReason === 'other' ? r.dischargeReasonOther || '' : '',
    };
  });

/** Keeps legacy top-level fields in sync with the first row for lists/reports. */
export const legacyAdmissionFieldsFromFirstRecord = (records, formatDateForBackend) => {
  const first = records?.[0];
  if (!first) {
    return {
      admission_date: '',
      admission_number: '',
      admitted_from: '',
      admitted_from_other: '',
      enrolled_date: '',
      referral_source: '',
      referral_source_other: '',
    };
  }
  return {
    admission_date: formatDateForBackend(first.admissionDate),
    admission_number: first.admissionNumber || '',
    admitted_from: first.admittedFrom || '',
    admitted_from_other: first.admittedFrom === 'other' ? first.admittedFromOther || '' : '',
    enrolled_date: formatDateForBackend(first.enrolledDate),
    referral_source: first.referralSource || '',
    referral_source_other: first.referralSource === 'other' ? first.referralSourceOther || '' : '',
  };
};
